import { useState } from 'react';
import { api } from '../api';
import { Field, Modal } from '../components/ui';

/**
 * Toplu hesap ekleme.
 *
 * Hesap kaybetmek bu yolun işletme maliyeti, düzeltilecek bir arıza değil — kullandığımız
 * kütüphanenin geliştiricileri ban riskini "yüksek" diye tanımlıyor ve Instagram'ın
 * davranışsal puanlamasının, istekler ne kadar dikkatli hızlandırılırsa hızlandırılsın
 * hesapları işaretleyebildiğini söylüyor. O yüzden iyileştirilmeye değer olan şey kaybı
 * önlemek değil, yerine koymayı ucuzlatmak.
 *
 * Sağlayıcının dosyası olduğu gibi kabul ediliyor; her seferinde elle biçim düzeltmek tam
 * da burada ortadan kaldırılan sürtünme.
 */
interface Sonuc {
  imported: number;
  results: Array<{ username: string; status: string; proxy?: string; reason?: string }>;
}

export function ImportAccounts({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [metin, setMetin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);

  const gonder = () => {
    if (sonuc) return onClose();
    let accounts: unknown;
    try {
      const parsed: unknown = JSON.parse(metin);
      // Tek hesap, hesap dizisi ya da {accounts:[...]} — üçü de kabul, çünkü sağlayıcılar
      // üçünü de gönderiyor ve hangisini yapıştırdığını düşünmek zorunda kalmamalısın.
      accounts = Array.isArray(parsed)
        ? parsed
        : (parsed as { accounts?: unknown }).accounts ?? [parsed];
    } catch {
      setError('Bu geçerli bir JSON değil. Dosyanın tamamını yapıştırdığından emin ol.');
      return;
    }

    setBusy(true);
    setError(null);
    api
      .post<Sonuc>('/v1/instagram/accounts/import', { accounts, assignProxies: true })
      .then((value) => {
        setSonuc(value);
        onDone();
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Toplu hesap ekle"
      submitLabel={sonuc ? 'Kapat' : busy ? 'Ekleniyor…' : 'Ekle'}
      onClose={onClose}
      onSubmit={gonder}
      busy={busy}
      error={error}
      wide
    >
      {!sonuc && (
        <>
          <Field
            label="Hesap dosyası (JSON)"
            hint="Sağlayıcının verdiği dosyayı olduğu gibi yapıştır. Tek hesap da olur, liste de."
          >
            <textarea
              value={metin}
              rows={12}
              spellCheck={false}
              placeholder='[{"account_info":{"username":"...","password":"...","secret_key_2fa":"..."}}]'
              onChange={(e) => setMetin(e.target.value)}
            />
          </Field>
          <p className="note">
            Her hesap kendi proxy'sine bağlanır. İki hesabı aynı adrese koymak, Instagram'ın
            onları birbirine bağlayıp biri düştüğünde diğerlerini de düşürmesinin en yaygın
            sebebi — o yüzden boş proxy kalmazsa kalan hesaplar eklenmez, ikiye katlanmaz.
          </p>
          <p className="note">
            Doğrulayıcı anahtarı (<code>secret_key_2fa</code>) dosyada varsa alınır; o
            hesaplar girişlerini kendileri yapar ve senden kod istemez.
          </p>
        </>
      )}

      {sonuc && (
        <>
          <p className="note">
            <strong>{sonuc.imported}</strong> hesap eklendi.
          </p>
          <div className="assisted-bar" style={{ display: 'block', maxHeight: 260, overflowY: 'auto' }}>
            {sonuc.results.map((r) => (
              <p key={r.username} className="assisted-url" style={{ whiteSpace: 'normal' }}>
                {r.status === 'imported' ? '✓' : '—'} {r.username}
                {r.proxy ? ` → ${r.proxy}` : ''}
                {r.reason ? ` (${r.reason})` : ''}
              </p>
            ))}
          </div>
          <p className="note">
            Hesaplar kapalı olarak eklendi. Her birinde <strong>Giriş yap</strong>'a basman
            yeterli — anahtarı olanlar kod sormadan girer. Hepsini arka arkaya deneme:
            Instagram giriş denemelerini adres bloğu başına sınırlıyor, aralıklı yap.
          </p>
        </>
      )}
    </Modal>
  );
}
